import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { usePermissions } from '@customerio/hydra-permissions/react';
import type { Customer, PeopleListParams } from '@/services/db/customer/entity';
import { getPeopleQueryOptions, useUnfilteredPeopleCountOptional } from '@/services/db/customer/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useAnalytics } from '@/services/analytics';
import { isEmptyString } from '@/utils/string';
import {
  areResultsHidden,
  isEmptyFiltersQuery,
  isValidPaginationPage,
  noFiltersApplied,
  summarizeFilterConditions,
} from './utils/list-params';

export const PER_PAGE = 50;

export type PeopleSearchBy = 'email' | 'filter';

const PEOPLE_LIST_PARSERS = {
  email: parseAsString.withDefault(''),
  filters: parseAsString,
  page: parseAsInteger.withDefault(1),
  source: parseAsString,
};

/** Analytics labels for the `source` attribution param carried by inbound links. */
const SOURCE_LABELS = new Map([
  ['segment_overview', 'Segment Overview Tab'],
  ['segment_people', 'Segment People Tab'],
]);

type PeopleListQuery = ReturnType<typeof usePeopleListQuery>;
type SetPeopleParams = ReturnType<typeof useQueryStates<typeof PEOPLE_LIST_PARSERS>>[1];

function usePeopleListQuery(workspaceId: number, { email, filters, page }: PeopleListQueryInputs) {
  const queryParams: PeopleListParams = useMemo(() => {
    const query: PeopleListParams = { page, sort: 'created_at', asc: false };
    if (filters != null && !isEmptyString(filters)) query.filters = filters;
    if (!isEmptyString(email)) query.email = email;
    return query;
  }, [page, filters, email]);

  // Always fresh (`staleTime: 0`): the add/merge flows are still Ember-owned,
  // so returning from them must never show a cached page. The list is what the
  // page renders, so a failed load is fatal rather than a permanent loader:
  // `throwOnError` sends it to the route error boundary (the same pattern as
  // `useSendingIdentities`).
  return useQuery({ ...getPeopleQueryOptions(workspaceId, queryParams), staleTime: 0, throwOnError: true });
}

interface PeopleListQueryInputs {
  email: string;
  filters: string | null;
  page: number;
}

type WelcomeDecision = 'welcome' | 'list' | null;

/**
 * `null` while undecided. The probe checks the unfiltered total (not row
 * count) because permissions can hide rows the workspace does have; a failed
 * probe falls back to the regular empty state.
 */
function resolveWelcomeDecision(
  listQuery: PeopleListQuery,
  probe: ReturnType<typeof useUnfilteredPeopleCountOptional>,
): WelcomeDecision {
  if (!listQuery.isSuccess) return null;
  if (listQuery.data.customers.length > 0) return 'list';
  if (probe.isSuccess) return probe.data === 0 ? 'welcome' : 'list';
  if (probe.isError) return 'list';
  return null;
}

/**
 * The welcome-vs-list decision. Latched: once the list renders it never flips
 * back to the welcome state within this visit, so the table (and anything the
 * user is interacting with) stays mounted across page/filter changes.
 */
function useWelcomeDecision(workspaceId: number, listQuery: PeopleListQuery): WelcomeDecision {
  const { track } = useAnalytics();
  const [decision, setDecision] = useState<WelcomeDecision>(null);

  const probe = useUnfilteredPeopleCountOptional({
    workspaceId,
    enabled: decision == null && listQuery.isSuccess && listQuery.data.customers.length === 0,
  });

  const resolved = decision ?? resolveWelcomeDecision(listQuery, probe);
  if (decision == null && resolved != null) {
    setDecision(resolved);
  }

  useEffect(() => {
    if (decision === 'welcome') {
      void track('people', 'emptyStateViewed', {});
    }
  }, [decision, track]);

  return decision;
}

/** Clamp an out-of-range page back to 1 once the result set is known (page 1 never needs rewriting). */
function usePageClamp(listQuery: PeopleListQuery, page: number, setParams: SetPeopleParams): void {
  const pagination = listQuery.data?.meta.pagination;
  useEffect(() => {
    if (pagination == null || page === 1) return;
    if (!isValidPaginationPage({ total: pagination.total, size: pagination.size, page })) {
      void setParams({ page: 1 }, { history: 'replace' });
    }
  }, [pagination, page, setParams]);
}

/**
 * The list-viewed analytics event, fired once per completed load; the
 * `source` attribution param is consumed (tracked once, then dropped from the
 * URL). The stamp ref keeps param changes between loads from re-firing the
 * event for a load already reported.
 */
function useListViewedTracking(
  listQuery: PeopleListQuery,
  { email, filters, source }: { email: string; filters: string | null; source: string | null },
  setParams: SetPeopleParams,
): void {
  const { track } = useAnalytics();
  const { dataUpdatedAt, isFetching } = listQuery;
  const lastTrackedStamp = useRef(0);
  useEffect(() => {
    // While fetching, any data on screen is a cached page whose load was
    // already reported — tracking it would double-count back-navigations.
    if (isFetching || dataUpdatedAt === 0 || lastTrackedStamp.current === dataUpdatedAt) return;
    lastTrackedStamp.current = dataUpdatedAt;
    const summary = summarizeFilterConditions(filters);
    void track('people', 'listViewedSearchedFiltered', {
      'Filter Operator': summary.operator,
      'Filtered by Email Address': !isEmptyString(email),
      'Email Filter Includes Wildcard': email.includes('*'),
      'Number of Segment Conditions': summary.segments,
      'Number of Attribute Conditions': summary.attributes,
      'Number of Newsletter Conditions': summary.newsletters,
      'Number of Message Conditions': summary.messages,
      'Number of Event Conditions': summary.events,
      'Number of Relationship Attribute Conditions': summary.relationship_attributes,
      // undefined serializes away, so an unattributed load carries no Source key.
      Source: source == null ? undefined : SOURCE_LABELS.get(source),
    });
    if (source != null) {
      void setParams({ source: null }, { history: 'replace' });
    }
  }, [isFetching, dataUpdatedAt, filters, email, source, track, setParams]);
}

/**
 * URL state, the list query, and everything derived from them for the People
 * list page: search mode, welcome-vs-list decision, hidden-results state,
 * page clamping, and the list-viewed analytics event.
 */
export function usePeopleListPage() {
  const workspaceId = requireWorkspaceId();
  const { hasPermission } = usePermissions();
  const [params, setParams] = useQueryStates(PEOPLE_LIST_PARSERS, { history: 'push' });
  const { email, filters, page, source } = params;

  const listQuery = usePeopleListQuery(workspaceId, { email, filters, page });
  const people: readonly Customer[] = listQuery.data?.customers ?? [];
  const pagination = listQuery.data?.meta.pagination;
  const totalCount = pagination?.total ?? 0;

  // Search mode: an active condition filter forces "filter"; otherwise the
  // user's explicit choice stands.
  const [searchByChoice, setSearchByChoice] = useState<PeopleSearchBy>('email');
  const activeSearchBy: PeopleSearchBy = !isEmptyFiltersQuery(filters) ? 'filter' : searchByChoice;

  const activateSearchBy = (searchBy: PeopleSearchBy) => {
    if (searchBy === activeSearchBy) return;
    setSearchByChoice(searchBy);
    void setParams({ email: '', filters: null });
  };

  const search = (term: string) => {
    // An unchanged term still refetches — the Search button always re-runs the query.
    const unchanged = email === term && page === 1;
    void setParams({ email: term, page: 1 });
    if (unchanged) void listQuery.refetch();
  };

  const clearFilters = () => {
    // Stay in conditions mode: without an active filter forcing it, the mode
    // falls back to the user's choice, which never became "filter" for a
    // deep-linked filters param.
    setSearchByChoice('filter');
    void setParams({ filters: null });
  };

  const welcomeDecision = useWelcomeDecision(workspaceId, listQuery);
  usePageClamp(listQuery, page, setParams);
  useListViewedTracking(listQuery, { email, filters, source }, setParams);

  const canEdit = hasPermission('journeys/people/edit');
  const hasHiddenResults =
    listQuery.isSuccess &&
    pagination != null &&
    areResultsHidden({ total: pagination.total, page: pagination.page, rowCount: people.length });

  return {
    workspaceId,
    email,
    filters,
    page,
    setPage: (nextPage: number) => void setParams({ page: nextPage }),
    activeSearchBy,
    activateSearchBy,
    search,
    clearFilters,
    listQuery,
    people,
    totalCount,
    message: listQuery.data?.meta.message ?? null,
    welcomeDecision,
    canEdit,
    hasHiddenResults,
    hasFilters: filters != null || !isEmptyString(email),
    noFiltersApplied: noFiltersApplied({ filters, email }),
    selectAllDisabled: !canEdit || totalCount < 0 || people.length === 0,
  };
}

/**
 * Row-selection state with the cross-page select-all semantics: checking
 * every row (or clicking Select all) marks the whole result set selected;
 * the raw flag survives per-row math so bulk actions can tell "all matching
 * the filter" from "all on this page". Selection resets whenever a load
 * completes, matching the previous page lifecycle.
 */
export function usePeopleSelection({
  people,
  totalCount,
  dataStamp,
  pendingDeletionIds,
}: {
  people: readonly Customer[];
  totalCount: number;
  /** `dataUpdatedAt` of the list query — a changed stamp clears the selection. */
  dataStamp: number;
  pendingDeletionIds?: ReadonlySet<string>;
}) {
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [allSelectedFlag, setAllSelectedFlag] = useState(false);

  const [prevStamp, setPrevStamp] = useState(dataStamp);
  if (dataStamp !== prevStamp) {
    setPrevStamp(dataStamp);
    setRowSelection({});
    setAllSelectedFlag(false);
  }

  const selectedPeople = useMemo(
    () => people.filter((person) => rowSelection[person.id] === true),
    [people, rowSelection],
  );

  const allSelected = (totalCount > 0 && totalCount === selectedPeople.length) || allSelectedFlag;

  const onRowSelectionChange = (selection: Record<string, boolean>) => {
    setRowSelection(selection);
    const selectedCount = Object.values(selection).filter(Boolean).length;
    setAllSelectedFlag(selectedCount === totalCount && totalCount > 0);
  };

  const onSelectAll = () => {
    const selection: Record<string, boolean> = {};
    for (const person of people) {
      if (pendingDeletionIds?.has(person.id) !== true) selection[person.id] = true;
    }
    setRowSelection(selection);
    setAllSelectedFlag(true);
  };

  const clearSelection = () => {
    setRowSelection({});
    setAllSelectedFlag(false);
  };

  return { rowSelection, onRowSelectionChange, selectedPeople, allSelected, onSelectAll, clearSelection };
}
